import isClearSignedItem from './isClearSignedItem';
import SmimeCustomError from './SmimeCustomError';
import { SmimeAttachmentType } from 'owa-attachment-model-store';
import { ClientItemId } from 'owa-client-ids';
import encode from 'owa-encoding-utils/lib/utils/base64/encode';
import { ClientItem } from 'owa-mail-store';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import ReadingPanePopoutItemFolderInfo from 'owa-popout-utils/lib/schema/ReadingPanePopoutItemFolderInfo';
import popoutReadingPane from 'owa-popout-utils/lib/utils/popoutReadingPane';
import { lazySmimeAdapter } from 'owa-smime-adapter';
import ISmimeAdapter from 'owa-smime-adapter/lib/adapter/ISmimeAdapter';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import SmimeMessage from 'owa-smime-adapter/lib/store/schema/SmimeMessage';
import SmimeResult from 'owa-smime-adapter/lib/store/schema/SmimeResult';
import { PKCS_MIME_TYPE, SMIME_FILE_EXTENSION } from 'owa-smime-adapter/lib/utils/constants';
import isSmimeAdapterUsable from 'owa-smime-adapter/lib/utils/isSmimeAdapterUsable';
import { getIdWithSmimePrefix } from 'owa-smime-adapter/lib/utils/smimeUtils';

/**
 * Utility responsible for creating message data from attachment info and then populating the item data in mail store
 * @param attachment
 * @param parentItemId
 */
export default async function loadSmimeItemAttachmentData(
    attachment: SmimeAttachmentType,
    parentItemId: ClientItemId
): Promise<void> {
    const parentItem = getMailStore().items.get(parentItemId.Id);
    const dateTimeReceived = parentItem ? parentItem.DateTimeReceived : '';
    const smimeAdapter: ISmimeAdapter = await lazySmimeAdapter.import();
    if (isSmimeAdapterUsable()) {
        const smimeResult: SmimeResult = await smimeAdapter.createMessageFromItemAttachmentAsync(
            attachment.MimeContent
        );
        if (smimeResult.ErrorCode == SmimeErrorStateEnum.NoError) {
            const smimeMessage = smimeResult.Data as SmimeMessage;
            const itemId = smimeMessage.ItemId.Id;
            const itemInfo = preparePopoutData(
                smimeMessage,
                dateTimeReceived,
                attachment.MimeContent
            );
            popoutReadingPane(itemId, null /*internetMessageId*/, null /* mailboxInfo */, itemInfo);
        } else {
            throw new SmimeCustomError(
                "S/MIME : loadSmimeItemAttachmentData : The MIME content of the item attachment can't be parsed.",
                smimeResult.ErrorCode
            );
        }
    } else {
        throw new Error('S/MIME : loadSmimeItemAttachmentData : Adapter could not be initialized');
    }
}

// exported only for testing purpose
export function preparePopoutData(
    smimeMessage: SmimeMessage,
    dateTimeReceived: string,
    mime: string
): ReadingPanePopoutItemFolderInfo {
    const clientItem = smimeMessage as ClientItem;
    // The control does not return dateTimeReceived of the item attachment, so we populate it with its parent's dateTimeReceived property
    clientItem.DateTimeReceived = dateTimeReceived;
    /**
     * If the attachment is clear signed, the S/MIME data package needs to be added.
     * For signed messages, the data package should be part of the normal attachment collection.
     * Otherwise we will not be able to verify the digital signature when the flow goes through loadItemReadingPane.
     */
    if (isClearSignedItem(clientItem)) {
        const smimeAttachmentName = `smime${SMIME_FILE_EXTENSION}`;
        const p7mAttachment: SmimeAttachmentType = {
            AttachmentId: { Id: getIdWithSmimePrefix() },
            Name: smimeAttachmentName,
            ContentType: PKCS_MIME_TYPE,
            Content: encode(mime),
            IsSmimeDecoded: false,
        };
        clientItem.Attachments = [p7mAttachment];
    }
    return {
        folderId: null, // We don't have the folder info for S/MIME item attachments
        treeType: null,
        smimeAttachmentItem: clientItem,
    };
}
